(function () {
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value);
  };

  const lockBody = (lock) => document.body.classList.toggle('is-locked', Boolean(lock));
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[character]);
  const cleanProductTitle = (value) => String(value || '')
    .replace(/^For (?:men|women) inspired by\s+/i, '')
    .replace(/^Our impression of\s+/i, '')
    .replace(/^Our impression\s+/i, '')
    .replace(/^Inspired by\s+/i, '')
    .replace(/^In(?:s|p)ired by\s+/i, '')
    .replace(/^Inspired\s+/i, '')
    .replace(/^Dupe of\s+/i, '')
    .replace(/^Similar to\s+/i, '')
    .trim();
  const extractInspiredBy = (value) => {
    const title = String(value || '').trim();
    const cleaned = cleanProductTitle(title);
    return cleaned && cleaned !== title ? cleaned : '';
  };
  const fixedSprayBundleVariantId = 46087185891501;
  const enableFixedSprayBundleConversion = false;
  const knownFragranceOilHandles = new Set([
    'musk-altahara',
    'frankincense-fragrances-oil',
    'our-impression-of-baccarat-rouge-541'
  ]);
  let isCheckoutBundleTransforming = false;
  let isAutoBundleConverting = false;
  let autoBundleConversionTimer = null;
  let manualBundleConversionPromise = null;

  const isFixedSprayBundleCartItem = (item) => {
    const handle = String(item?.handle || '').toLowerCase();
    const productType = String(item?.product_type || '').toLowerCase();
    const sku = String(item?.sku || '').toLowerCase();
    return handle === '3-spray-bundle' || productType.includes('bundle') || sku === 'ip-bundle-3x50ml-125';
  };

  const isManualBundleEligibleCartItem = (item) => {
    if (!item || isFixedSprayBundleCartItem(item)) return false;
    const handle = String(
      item.handle || String(item.url || '').split('/products/')[1]?.split(/[?#/]/)[0] || ''
    ).toLowerCase();
    if (knownFragranceOilHandles.has(handle)) return false;

    const searchable = [
      item.product_title,
      item.title,
      item.handle,
      item.product_type,
      item.variant_title,
      item.url
    ].join(' ').toLowerCase();
    const isExcludedFormat = [
      'discovery',
      'sample',
      'fragrance oil',
      'perfume oil',
      'roll on',
      'roll-on',
      '10 ml',
      '10ml',
      'gift card',
      'gift-card',
      'shipping protection',
      'order protection',
      'package protection',
      'protection',
      'insurance',
      'route',
      'navidium',
      'warranty'
    ].some((term) => searchable.includes(term));
    if (isExcludedFormat) return false;

    return true;
  };

  const getManualBundleEligibleUnits = (cart) => {
    const units = [];
    (cart?.items || []).forEach((item) => {
      if (!isManualBundleEligibleCartItem(item)) return;
      const quantity = Math.max(Number(item.quantity || 0), 0);
      const title = cleanProductTitle(item.product_title || item.title || 'Selected scent');
      for (let index = 0; index < quantity; index += 1) {
        units.push({
          key: item.key,
          variantId: item.variant_id,
          title: title || item.product_title || item.title || 'Selected scent'
        });
      }
    });
    return units;
  };

  const buildFixedSprayBundleProperties = (group) => group.reduce((properties, item, index) => {
    const position = index + 1;
    properties[`Scent ${position}`] = item.title;
    properties[`_Scent ${position} Variant ID`] = item.variantId;
    return properties;
  }, { Bundle: 'Legacy fixed spray bundle' });

  async function convertManualSpraysToFixedBundles(cart) {
    const eligibleUnits = getManualBundleEligibleUnits(cart);
    const bundleCount = Math.floor(eligibleUnits.length / 3);
    if (!bundleCount) return false;

    const consumedByKey = {};
    const bundleItems = [];
    for (let bundleIndex = 0; bundleIndex < bundleCount; bundleIndex += 1) {
      const group = eligibleUnits.slice(bundleIndex * 3, bundleIndex * 3 + 3);
      group.forEach((unit) => {
        consumedByKey[unit.key] = (consumedByKey[unit.key] || 0) + 1;
      });
      bundleItems.push({
        id: fixedSprayBundleVariantId,
        quantity: 1,
        properties: buildFixedSprayBundleProperties(group)
      });
    }

    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: bundleItems })
    });
    const addState = await addResponse.json().catch(() => ({}));
    if (!addResponse.ok || addState.status) {
      throw new Error(addState.description || 'Unable to prepare bundle pricing.');
    }

    const updates = {};
    (cart.items || []).forEach((item) => {
      const consumed = consumedByKey[item.key] || 0;
      if (!consumed) return;
      updates[item.key] = Math.max(Number(item.quantity || 0) - consumed, 0);
    });

    if (Object.keys(updates).length) {
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      const updateState = await updateResponse.json().catch(() => ({}));
      if (!updateResponse.ok || updateState.status) {
        throw new Error(updateState.description || 'Unable to finalize bundle pricing.');
      }
    }

    return true;
  }

  async function refreshCartAfterManualBundleConversion() {
    const dawnDrawer = getDawnCartDrawer();
    if (dawnDrawer) {
      const sectionsToRender = dawnDrawer.getSectionsToRender().map((section) => section.id);
      const sectionResponse = await fetch(`${window.routes?.cart_url || '/cart'}?sections=${sectionsToRender.join(',')}`);
      const sections = await sectionResponse.json();
      if (sectionResponse.ok && sections) {
        dawnDrawer.renderContents({ id: fixedSprayBundleVariantId, sections });
        return;
      }
    }

    const cartPath = (window.routes?.cart_url || '/cart').replace(/\/$/, '');
    const currentPath = window.location.pathname.replace(/\/$/, '');
    if (currentPath === cartPath) {
      window.location.reload();
      return;
    }

    await fetchCart();
  }

  async function runManualSprayBundleConversion({ refreshCart = true } = {}) {
    if (!enableFixedSprayBundleConversion) return false;
    if (manualBundleConversionPromise) return manualBundleConversionPromise;

    manualBundleConversionPromise = (async () => {
      isAutoBundleConverting = true;
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      if (!response.ok || cart.status) return false;
      const converted = await convertManualSpraysToFixedBundles(cart);
      if (converted && refreshCart) {
        refreshCartAfterManualBundleConversion().catch((error) => {
          console.warn('Unable to refresh cart after bundle conversion', error);
        });
      }
      return converted;
    })();

    try {
      return await manualBundleConversionPromise;
    } finally {
      manualBundleConversionPromise = null;
      isAutoBundleConverting = false;
    }
  }

  async function autoConvertManualSprayBundles() {
    if (isCheckoutBundleTransforming) return false;

    try {
      return await runManualSprayBundleConversion({ refreshCart: true });
    } catch (error) {
      console.warn('Automatic bundle cart conversion failed', error);
      return false;
    }
  }

  function scheduleManualSprayBundleConversion(delay = 0) {
    if (!enableFixedSprayBundleConversion) return;
    window.clearTimeout(autoBundleConversionTimer);
    autoBundleConversionTimer = window.setTimeout(autoConvertManualSprayBundles, delay);
  }

  async function prepareManualSprayBundlesForCheckout() {
    if (!enableFixedSprayBundleConversion) return false;
    window.clearTimeout(autoBundleConversionTimer);
    return runManualSprayBundleConversion({ refreshCart: false });
  }

  async function syncManualBundleAcceleratedCheckout() {
    const dynamicCheckoutButtons = qsa('.cart__dynamic-checkout-buttons');
    if (!dynamicCheckoutButtons.length) return;

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      if (!response.ok || cart.status) return;
      const hasManualBundleReady = getManualBundleEligibleUnits(cart).length >= 3;
      dynamicCheckoutButtons.forEach((wrapper) => {
        wrapper.hidden = hasManualBundleReady;
        wrapper.setAttribute('aria-hidden', hasManualBundleReady ? 'true' : 'false');
      });
    } catch (error) {
      console.warn('Unable to sync accelerated checkout visibility', error);
    }
  }

  function initAnnouncement() {
    const rotators = qsa('[data-announcement-rotator]');
    rotators.forEach((rotator) => {
      const messages = qsa('[data-announcement-message]', rotator);
      if (messages.length < 2) return;
      let index = 0;
      messages.forEach((message, i) => message.classList.toggle('is-active', i === 0));
      window.setInterval(() => {
        messages[index].classList.remove('is-active');
        index = (index + 1) % messages.length;
        messages[index].classList.add('is-active');
      }, 3400);
    });
  }

  function initMobileMenu() {
    const menu = qs('[data-mobile-menu]');
    if (!menu) return;
    const open = () => {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      lockBody(true);
    };
    const close = () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      lockBody(false);
    };
    qsa('[data-menu-open]').forEach((button) => button.addEventListener('click', open));
    qsa('[data-menu-close]').forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) close();
    });
  }

  function initHeaderMegaMenus() {
    const items = qsa('.ip-header-nav .ip-nav-item.has-mega');
    if (!items.length) return;

    const closeItem = (item) => {
      item.classList.remove('is-open');
      qs(':scope > a', item)?.setAttribute('aria-expanded', 'false');
    };

    const openItem = (item) => {
      items.forEach((otherItem) => {
        if (otherItem !== item) closeItem(otherItem);
      });
      item.classList.add('is-open');
      qs(':scope > a', item)?.setAttribute('aria-expanded', 'true');
    };

    items.forEach((item) => {
      const trigger = qs(':scope > a', item);
      const menu = qs('.ip-mega-menu', item);
      if (!trigger || !menu) return;

      item.addEventListener('pointerenter', () => openItem(item));
      item.addEventListener('pointerleave', () => closeItem(item));
      trigger.addEventListener('focus', () => openItem(item));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) closeItem(item);
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeItem(item);
          trigger.focus();
        }
        if (event.key === 'ArrowDown' && document.activeElement === trigger) {
          const firstLink = qs('a', menu);
          if (firstLink) {
            event.preventDefault();
            openItem(item);
            firstLink.focus();
          }
        }
      });
    });

    document.addEventListener('click', (event) => {
      if (!items.some((item) => item.contains(event.target))) {
        items.forEach(closeItem);
      }
    });
  }

  function initSearchModalReliability() {
    qsa('details-modal.header__search').forEach((modal) => {
      const details = qs('details', modal);
      const input = qs('input[type="search"]', modal);
      if (!details || !input) return;

      details.addEventListener('toggle', () => {
        if (!details.open) return;
        window.setTimeout(() => {
          input.focus({ preventScroll: true });
          if (input.value) input.setSelectionRange(input.value.length, input.value.length);
        }, 80);
      });
    });
  }

  function initMobileDrawerEscapeReliability() {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const drawerDetails = qs('#Details-menu-drawer-container[open]');
      if (!drawerDetails) return;

      const drawer = drawerDetails.closest('header-drawer');
      const summary = qs(':scope > summary', drawerDetails);
      if (drawer && typeof drawer.closeMenuDrawer === 'function') {
        drawer.closeMenuDrawer(event, summary);
      } else {
        drawerDetails.removeAttribute('open');
        drawerDetails.classList.remove('menu-opening');
        qsa('details[open]', drawerDetails).forEach((details) => {
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
        });
        summary?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-mobile', 'overflow-hidden-desktop');
      }
    });
  }

  function initCheckoutHandoffReliability() {
    if (!enableFixedSprayBundleConversion) return;
    syncManualBundleAcceleratedCheckout();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        scheduleManualSprayBundleConversion(0);
      });
    }
    qsa('[data-cart-open], #cart-icon-bubble').forEach((trigger) => {
      trigger.addEventListener('click', () => scheduleManualSprayBundleConversion(0));
    });

    document.addEventListener('click', async (event) => {
      const button = event.target.closest('button[name="checkout"]');
      const isCartCheckout = Boolean(button?.matches('.cart__checkout-button') || button?.closest('#cart-notification-form'));
      if (!button || !isCartCheckout || button.disabled || isCheckoutBundleTransforming) return;

      event.preventDefault();
      event.stopPropagation();

      const originalText = button.textContent;
      isCheckoutBundleTransforming = true;
      button.disabled = true;
      button.textContent = 'Preparing bundle...';

      try {
        await prepareManualSprayBundlesForCheckout();
        window.location.assign('/checkout');
      } catch (error) {
        console.warn('Bundle checkout preparation failed', error);
        button.disabled = false;
        button.textContent = originalText || 'Secure Checkout';
        window.alert(error?.message || 'Unable to prepare bundle pricing. Please try again.');
      } finally {
        isCheckoutBundleTransforming = false;
      }
    });
  }

  function renderCart(cart) {
    const count = qsa('[data-cart-count]');
    count.forEach((node) => {
      const nextCount = String(cart.item_count);
      const previousCount = node.dataset.ipLastCount;
      node.textContent = cart.item_count;
      node.setAttribute('aria-label', `${cart.item_count} items in cart`);
      node.dataset.ipLastCount = nextCount;
      if (previousCount && previousCount !== nextCount) {
        node.classList.remove('ip-count-pop');
        window.requestAnimationFrame(() => node.classList.add('ip-count-pop'));
      }
    });

    const content = qs('[data-cart-content]');
    const subtotal = qs('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    if (!content) return;

    if (!cart.items.length) {
      content.innerHTML = `
        <div class="cart-empty">
          <h3>Your cart is empty.</h3>
          <p>Find your next signature scent.</p>
          <a class="button button--primary" href="/collections/all?sort_by=best-selling">Shop Best Sellers</a>
        </div>
      `;
    } else {
      content.innerHTML = `
        <div class="cart-items">
          ${cart.items.map((item, index) => `
            <div class="cart-item" data-line="${escapeHtml(index + 1)}">
              <a class="cart-item__image" href="${escapeHtml(item.url)}">
                ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(cleanProductTitle(item.product_title))}">` : ''}
              </a>
              <div class="cart-item__info">
                <a class="cart-item__title" href="${escapeHtml(item.url)}">${escapeHtml(cleanProductTitle(item.product_title))}</a>
                <p>${formatMoney(item.final_line_price)}</p>
                <div class="cart-item__controls">
                  <button class="quantity-button" type="button" data-cart-qty data-line="${escapeHtml(index + 1)}" data-quantity="${escapeHtml(item.quantity - 1)}" aria-label="Decrease quantity">-</button>
                  <span>${escapeHtml(item.quantity)}</span>
                  <button class="quantity-button" type="button" data-cart-qty data-line="${escapeHtml(index + 1)}" data-quantity="${escapeHtml(item.quantity + 1)}" aria-label="Increase quantity">+</button>
                  <button class="cart-item__remove" type="button" data-cart-qty data-line="${escapeHtml(index + 1)}" data-quantity="0" aria-label="Remove ${escapeHtml(cleanProductTitle(item.product_title))}">Remove</button>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }

    const progress = qs('[data-free-shipping-threshold]');
    if (progress) {
      const threshold = Number(progress.dataset.freeShippingThreshold || 0);
      const percent = threshold ? Math.min(100, cart.total_price / threshold * 100) : 0;
      const text = qs('.cart-progress__text', progress);
      const bar = qs('.cart-progress__bar span', progress);
      if (bar) bar.style.width = `${percent}%`;
      if (text && threshold) {
        text.textContent = cart.total_price >= threshold
          ? 'You qualify for free shipping.'
          : `You're ${formatMoney(threshold - cart.total_price)} away from free shipping.`;
      }
    }
  }

  async function fetchCart() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  }

  function getDawnCartDrawer() {
    const drawer = qs('cart-drawer');
    return drawer && typeof drawer.renderContents === 'function' ? drawer : null;
  }

  function appendDawnCartSections(formData) {
    const drawer = getDawnCartDrawer();
    if (!drawer) return null;
    formData.append('sections', drawer.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    drawer.setActiveElement(document.activeElement);
    return drawer;
  }

  function openCart() {
    const dawnDrawer = getDawnCartDrawer();
    if (dawnDrawer) {
      dawnDrawer.open();
      return;
    }
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockBody(true);
  }

  function closeCart() {
    const dawnDrawer = getDawnCartDrawer();
    if (dawnDrawer) {
      dawnDrawer.close();
      return;
    }
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    lockBody(false);
  }

  function initCart() {
    qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', async (event) => {
      event.preventDefault();
      await fetchCart();
      openCart();
    }));
    qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('.js-product-form');
      if (!form) return;
      event.preventDefault();
      const button = qs('button[type="submit"]', form);
      const original = button ? button.textContent : '';
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding...';
      }
      try {
        const formData = new FormData(form);
        const dawnDrawer = appendDawnCartSections(formData);
        const response = await fetch('/cart/add.js', { method: 'POST', body: formData });
        const parsedState = await response.json();
        if (dawnDrawer && parsedState.sections) {
          dawnDrawer.renderContents(parsedState);
        } else {
          await fetchCart();
          openCart();
        }
        scheduleManualSprayBundleConversion(0);
        if (button) {
          button.classList.add('ip-added');
          window.setTimeout(() => button.classList.remove('ip-added'), 820);
        }
      } catch (error) {
        if (button) button.textContent = 'Try again';
      } finally {
        if (button) {
          window.setTimeout(() => {
            button.disabled = false;
            button.textContent = original;
          }, 700);
        }
      }
    });

    document.addEventListener('click', async (event) => {
      const qty = event.target.closest('[data-cart-qty]');
      if (!qty) return;
      const line = Number(qty.dataset.line);
      const quantity = Number(qty.dataset.quantity);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      await fetchCart();
      scheduleManualSprayBundleConversion(0);
    });

    document.addEventListener('keydown', (event) => {
      const drawer = qs('[data-cart-drawer]');
      const dawnDrawer = getDawnCartDrawer();
      const isDawnDrawerOpen = dawnDrawer && dawnDrawer.classList.contains('active');
      if (event.key === 'Escape' && ((drawer && drawer.classList.contains('is-open')) || isDawnDrawerOpen)) closeCart();
    });
  }

  function initQuantitySelectors() {
    qsa('[data-quantity-wrapper]').forEach((wrapper) => {
      const input = qs('input', wrapper);
      qsa('[data-quantity-change]', wrapper).forEach((button) => {
        button.addEventListener('click', () => {
          const direction = Number(button.dataset.quantityChange);
          const next = Math.max(1, Number(input.value || 1) + direction);
          input.value = next;
        });
      });
    });
  }

  function initProductGallery() {
    qsa('[data-product-gallery]').forEach((gallery) => {
      const main = qs('.product-gallery__main img', gallery);
      const link = qs('[data-gallery-link]', gallery);
      qsa('[data-gallery-thumb]', gallery).forEach((button) => {
        button.addEventListener('click', () => {
          qsa('[data-gallery-thumb]', gallery).forEach((thumb) => thumb.classList.remove('is-active'));
          button.classList.add('is-active');
          if (main) {
            main.src = button.dataset.image;
            main.alt = button.dataset.alt || '';
          }
          if (link) link.href = button.dataset.full || button.dataset.image;
        });
      });
    });
  }

  function initFilters() {
    qsa('[data-filter-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = qs(button.dataset.filterToggle);
        if (panel) panel.classList.toggle('is-open');
      });
    });
  }

  function initBundleBuilder() {
    qsa('[data-bundle-builder]').forEach((builder) => {
      document.body.classList.add('ip-bundle-page');
      const min = Number(builder.dataset.min || 3);
      const max = Number(builder.dataset.max || 5);
      const tierOneCount = Number(builder.dataset.tierOneCount || min);
      const tierTwoCount = Number(builder.dataset.tierTwoCount || max);
      const tierOneDiscount = Number(builder.dataset.tierOneDiscount || 15);
      const tierTwoDiscount = Number(builder.dataset.tierTwoDiscount || 20);
      const tierOneDiscountCode = builder.dataset.tierOneDiscountCode || '';
      const tierTwoDiscountCode = builder.dataset.tierTwoDiscountCode || '';
      const selected = [];
      const progressText = qs('[data-bundle-progress-text]', builder);
      const progressBar = qs('[data-bundle-progress-bar]', builder);
      const selectedList = qs('[data-selected-bundle]', builder);
      const addButtons = qsa('[data-add-bundle], [data-add-bundle-mobile]', builder);
      const mobileCount = qs('[data-bundle-mobile-count]', builder);
      const mobilePreview = qs('[data-bundle-mobile-preview]', builder);
      const tierMessage = qs('[data-bundle-tier-message]', builder);
      const subtotalNode = qs('[data-bundle-subtotal]', builder);
      const savingsNode = qs('[data-bundle-savings]', builder);
      const totalNode = qs('[data-bundle-total]', builder);
      const status = qs('[data-bundle-status]', builder);
      const paginationState = qs('[data-bundle-pagination-state]', builder);
      let isAdding = false;

      if (paginationState) {
        const currentPage = Number(paginationState.dataset.currentPage || 1);
        const totalPages = Math.max(Number(paginationState.dataset.totalPages || 1), 1);
        const usesAllFallback = paginationState.dataset.sourceFallback === 'true';
        const params = new URLSearchParams(window.location.search);

        if (params.has('page') && (currentPage > totalPages || (usesAllFallback && currentPage > 1))) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('page');
          window.location.replace(cleanUrl.toString());
          return;
        }
      }

      qsa('[data-bundle-filter]', builder).forEach((tab) => {
        tab.addEventListener('click', () => {
          const filter = tab.dataset.bundleFilter || 'all';
          qsa('[data-bundle-filter]', builder).forEach((button) => {
            button.classList.toggle('is-active', button === tab);
            button.setAttribute('aria-pressed', button === tab ? 'true' : 'false');
          });
          qsa('.bundle-product-card', builder).forEach((card) => {
            const categories = String(card.dataset.bundleCategories || 'all').split(/\s+/);
            card.hidden = filter !== 'all' && !categories.includes(filter);
          });
        });
      });

      const getVariantCount = (variantId) => selected.filter((item) => item.variantId === variantId).length;

      const getCurrentTier = () => {
        if (selected.length >= tierTwoCount) {
          return {
            count: tierTwoCount,
            discount: tierTwoDiscount,
            code: tierTwoDiscountCode,
            label: `${tierTwoDiscount}% off`
          };
        }
        if (selected.length >= tierOneCount) {
          return {
            count: tierOneCount,
            discount: tierOneDiscount,
            code: tierOneDiscountCode,
            label: `${tierOneDiscount}% off`
          };
        }
        return {
          count: 0,
          discount: 0,
          code: '',
          label: 'No discount yet'
        };
      };

      const getTierMessage = () => {
        if (selected.length >= tierTwoCount) {
          return `${tierTwoDiscount}% off unlocked. Add your bundle and the eligible savings will be ready at checkout.`;
        }
        if (selected.length >= tierOneCount) {
          const needed = tierTwoCount - selected.length;
          return `${tierOneDiscount}% off unlocked. Add ${needed} more scent${needed === 1 ? '' : 's'} to unlock ${tierTwoDiscount}% off.`;
        }
        const needed = tierOneCount - selected.length;
        return `Add ${needed} more scent${needed === 1 ? '' : 's'} to unlock ${tierOneDiscount}% off.`;
      };

      const syncAddButtons = () => {
        const ready = selected.length >= min;
        const tier = getCurrentTier();
        addButtons.forEach((button) => {
          const readyLabel = button.dataset.readyLabel || 'Add Bundle to Cart';
          const waitingLabel = button.dataset.waitingLabel || `Select ${min} scents`;
          button.disabled = isAdding || !ready;
          button.textContent = isAdding ? 'Adding bundle...' : ready ? `${readyLabel} - ${tier.label}` : waitingLabel;
        });
      };

      const renderSelectedSlots = () => Array.from({ length: max }, (_, index) => {
        const item = selected[index];
        if (!item) {
          let unlockLabel = 'Select a product';
          if (index + 1 === tierOneCount) unlockLabel = `Select a product - Unlock ${tierOneDiscount}% Off`;
          if (index + 1 === tierTwoCount) unlockLabel = `Select a product - Unlock ${tierTwoDiscount}% Off`;
          return `
            <div class="selected-bundle__slot is-empty">
              <span>Slot ${index + 1}</span>
              <strong>${unlockLabel}</strong>
            </div>
          `;
        }
        const image = item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy">` : '';
        return `
          <div class="selected-bundle__slot">
            ${image}
            <span>Slot ${index + 1}</span>
            <strong>${escapeHtml(item.title)}</strong>
            <button class="selected-bundle__remove" type="button" data-remove-bundle-index="${index}" aria-label="Remove ${escapeHtml(item.title)}">Remove</button>
          </div>
        `;
      }).join('');

      const update = () => {
        qsa('[data-bundle-select]', builder).forEach((button) => {
          const variantId = button.dataset.variantId;
          const selectedCount = getVariantCount(variantId);
          const isUnavailable = button.dataset.available === 'false';
          if (isUnavailable) {
            button.classList.remove('button--secondary');
            button.textContent = '+';
            button.disabled = true;
            return;
          }

          const bundleFull = selected.length >= max;
          button.classList.toggle('button--secondary', selectedCount > 0);
          button.textContent = '+';
          button.setAttribute('aria-label', selectedCount > 0 ? `Add another ${button.dataset.productTitle}` : `Add ${button.dataset.productTitle}`);
          button.disabled = bundleFull;
        });

        qsa('[data-bundle-decrement]', builder).forEach((button) => {
          const variantId = button.dataset.variantId;
          const selectedCount = getVariantCount(variantId);
          button.disabled = selectedCount === 0;
          button.setAttribute('aria-label', `Remove one ${button.dataset.productTitle}`);
        });

        qsa('[data-bundle-count]', builder).forEach((node) => {
          node.textContent = getVariantCount(node.dataset.variantId);
        });

        const tier = getCurrentTier();
        const subtotal = selected.reduce((sum, item) => sum + Number(item.price || 0), 0);
        const savings = Math.round(subtotal * (tier.discount / 100));
        const estimatedTotal = Math.max(subtotal - savings, 0);

        if (progressText) progressText.textContent = `${selected.length} of ${max} selected`;
        if (tierMessage) tierMessage.textContent = getTierMessage();
        if (subtotalNode) subtotalNode.textContent = formatMoney(subtotal);
        if (savingsNode) savingsNode.textContent = selected.length >= min ? `-${formatMoney(savings)}` : formatMoney(0);
        if (totalNode) totalNode.textContent = selected.length >= min ? formatMoney(estimatedTotal) : formatMoney(subtotal);
        if (mobileCount) mobileCount.textContent = `${selected.length} of ${max} selected`;
        if (mobilePreview) {
          mobilePreview.textContent = selected.length ? getTierMessage() : `Select ${min} scents to unlock ${tierOneDiscount}% off`;
        }
        if (progressBar) progressBar.style.width = `${selected.length / max * 100}%`;
        syncAddButtons();

        if (selectedList) {
          selectedList.innerHTML = renderSelectedSlots();
        }
      };

      builder.addEventListener('click', async (event) => {
        const select = event.target.closest('[data-bundle-select]');
        const decrement = event.target.closest('[data-bundle-decrement]');
        const remove = event.target.closest('[data-remove-bundle-index]');

        if (select) {
          if (select.dataset.available === 'false') return;
          if (selected.length >= max) return;
          const item = {
            variantId: select.dataset.variantId,
            title: select.dataset.productTitle,
            image: select.dataset.productImage || '',
            price: Number(select.dataset.productPrice || 0)
          };
          selected.push(item);
          if (status) status.textContent = '';
          update();
        }

        if (decrement) {
          const index = selected.map((item) => item.variantId).lastIndexOf(decrement.dataset.variantId);
          if (index >= 0) selected.splice(index, 1);
          if (status) status.textContent = '';
          update();
        }

        if (remove) {
          const index = Number(remove.dataset.removeBundleIndex);
          if (Number.isInteger(index) && index >= 0) selected.splice(index, 1);
          update();
        }
      });

      const addBundle = async () => {
        if (selected.length < min) return;
        isAdding = true;
        syncAddButtons();
        try {
          const dawnDrawer = getDawnCartDrawer();
          const sectionsToRender = dawnDrawer ? dawnDrawer.getSectionsToRender().map((section) => section.id) : [];
          const tier = getCurrentTier();
          const grouped = selected.reduce((items, item) => {
            const existing = items.find((entry) => entry.id === Number(item.variantId));
            if (existing) {
              existing.quantity += 1;
            } else {
              items.push({
                id: Number(item.variantId),
                quantity: 1,
                properties: {
                  '_Bundle Builder': 'Tiered spray bundle',
                  '_Bundle Tier': tier.label,
                  '_Bundle Quantity': String(selected.length)
                }
              });
            }
            return items;
          }, []);
          const payload = { items: grouped };
          if (dawnDrawer) {
            payload.sections = sectionsToRender.join(',');
            payload.sections_url = window.location.pathname;
            dawnDrawer.setActiveElement(document.activeElement);
          }
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const parsedState = await response.json();
          if (!response.ok || parsedState.status) {
            throw new Error(parsedState.description || 'Unable to add bundle');
          }
          if (tier.code) {
            await fetch(`/discount/${encodeURIComponent(tier.code)}`);
          }
          if (dawnDrawer && !parsedState.sections && sectionsToRender.length) {
            const sectionUrl = new URL(window.location.href);
            sectionUrl.searchParams.set('sections', sectionsToRender.join(','));
            const sectionResponse = await fetch(`${sectionUrl.pathname}${sectionUrl.search}`);
            parsedState.sections = await sectionResponse.json();
          }
          if (dawnDrawer && parsedState.sections) {
            dawnDrawer.renderContents(parsedState);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
            return;
          }
          if (status) status.textContent = `${tier.label} bundle added to cart.`;
        } catch (error) {
          if (status) status.textContent = error?.message || 'Something went wrong. Please try again.';
          console.warn('Bundle add failed', error);
        } finally {
          isAdding = false;
          syncAddButtons();
        }
      };

      addButtons.forEach((button) => button.addEventListener('click', addBundle));

      const params = new URLSearchParams(window.location.search);
      const preselect = params.get('variant');
      if (preselect) {
        const button = qs(`[data-bundle-select][data-variant-id="${preselect}"]`, builder);
        if (button) {
          selected.push({
            variantId: button.dataset.variantId,
            title: button.dataset.productTitle,
            image: button.dataset.productImage || '',
            price: Number(button.dataset.productPrice || 0)
          });
        }
      }
      update();
    });
  }

  function buildKeywordMap(source) {
    const map = {};
    Object.keys(source || {}).forEach((key) => {
      map[key] = String(source[key] || '').toLowerCase().split(',').map((item) => item.trim()).filter(Boolean);
    });
    return map;
  }

  function initQuiz() {
    qsa('[data-quiz]').forEach((quiz) => {
      const steps = qsa('[data-quiz-step]', quiz);
      const results = qs('[data-quiz-results]', quiz);
      const progress = qs('[data-quiz-progress]', quiz);
      const progressText = qs('[data-quiz-progress-text]', quiz);
      const resultGrid = qs('[data-quiz-result-grid]', quiz);
      const emailForm = qs('.quiz-email-form', quiz);
      const emailInput = qs('[data-quiz-email-input]', quiz);
      const emailMessage = qs('[data-quiz-email-message]', quiz);
      const emailSubmit = qs('[data-quiz-email-submit]', quiz);
      const emailResult = qs('[data-quiz-email-result]', quiz);
      const productsScript = qs('[data-quiz-products]', quiz);
      const keywordScript = qs('[data-quiz-keywords]', quiz);
      let products = [];
      let keywordMap = {};
      try {
        products = productsScript ? JSON.parse(productsScript.textContent || '[]') : [];
        keywordMap = buildKeywordMap(keywordScript ? JSON.parse(keywordScript.textContent || '{}') : {});
      } catch (error) {
        products = [];
        keywordMap = {};
      }
      const answers = {};
      const emailState = { status: 'none', value: '' };
      let lastQuizResult = null;
      const labels = {
        style: {
          fresh: 'Fresh and clean',
          sweet: 'Sweet and warm',
          woody: 'Woody and rich',
          floral: 'Floral and soft',
          spicy: 'Spicy and bold'
        },
        occasion: {
          everyday: 'everyday wear',
          work: 'work or school',
          date: 'date night',
          'going-out': 'going out',
          gifting: 'gifting'
        },
        intensity: {
          soft: 'soft and close to skin',
          balanced: 'balanced and noticeable',
          strong: 'strong and attention-grabbing'
        },
        impression: {
          clean: 'clean and put together',
          warm: 'warm and inviting',
          confident: 'confident and bold',
          polished: 'expensive and polished',
          unique: 'unique and memorable'
        },
        notes: {
          citrus: 'citrus and fresh',
          vanilla: 'vanilla and sweet',
          amber: 'amber and warm',
          woods: 'woods and oud',
          powdery: 'floral and powdery',
          musk: 'musk and clean'
        },
        email: {
          skipped: 'email skipped',
          submitted: 'email saved',
          failed: 'email not saved',
          none: 'email not provided'
        }
      };
      let index = 0;

      const showStep = (nextIndex) => {
        index = nextIndex;
        steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
        if (results) results.classList.remove('is-active');
        quiz.classList.remove('has-results');
        const percent = steps.length ? (index + 1) / steps.length * 100 : 0;
        if (progress) progress.style.width = `${percent}%`;
        if (progressText) {
          const activeStep = steps[index];
          progressText.textContent = activeStep && activeStep.dataset.progressLabel
            ? activeStep.dataset.progressLabel
            : `Step ${Math.min(index + 1, steps.length)} of ${steps.length}`;
        }
      };

      const labelFor = (group, value) => labels[group] && labels[group][value] ? labels[group][value] : String(value || '');

      const haystackFor = (product) => `${product.title} ${product.displayTitle} ${product.inspiredBy} ${product.tags} ${product.searchText} ${product.family} ${product.bestFor}`.toLowerCase();

      const answerAttributes = {
        style: {
          fresh: ['fresh', 'clean', 'citrus', 'musk'],
          sweet: ['sweet', 'warm', 'vanilla', 'gourmand'],
          woody: ['woody', 'rich', 'woods', 'oud'],
          floral: ['floral', 'soft', 'powdery'],
          spicy: ['spicy', 'bold', 'amber', 'warm']
        },
        occasion: {
          everyday: ['everyday', 'daily', 'easy-wear'],
          work: ['work', 'school', 'office-safe', 'daily'],
          date: ['date-night', 'romantic', 'evening'],
          'going-out': ['going-out', 'night-out', 'statement'],
          gifting: ['gifting', 'gift-safe', 'crowd-pleaser']
        },
        intensity: {
          soft: ['soft'],
          balanced: ['balanced'],
          strong: ['strong']
        },
        impression: {
          clean: ['clean', 'put-together'],
          warm: ['warm', 'inviting'],
          confident: ['confident', 'bold'],
          polished: ['polished', 'expensive'],
          unique: ['unique', 'memorable']
        },
        notes: {
          citrus: ['citrus', 'fresh'],
          vanilla: ['vanilla', 'sweet'],
          amber: ['amber', 'warm'],
          woods: ['woods', 'oud', 'woody'],
          powdery: ['floral', 'powdery'],
          musk: ['musk', 'clean']
        }
      };

      const quizProfiles = [
        {
          id: 'fresh-clean-everyday',
          label: 'Fresh Clean Everyday',
          triggers: { style: ['fresh'], occasion: ['everyday', 'work'], intensity: ['soft', 'balanced'], impression: ['clean'], notes: ['citrus', 'musk'] },
          scentFamilies: ['fresh', 'clean', 'citrus', 'musk'],
          notes: ['citrus', 'fresh', 'musk', 'clean'],
          occasions: ['everyday', 'work', 'school', 'office-safe', 'daily'],
          strengths: ['soft', 'balanced'],
          impressions: ['clean', 'put-together'],
          avoid: ['heavy-oud', 'dark-spicy', 'very-sweet'],
          description: 'A fresh and clean everyday scent with citrus or musk notes, made to feel put together, easy to wear, and not overpowering.'
        },
        {
          id: 'sweet-warm-date-night',
          label: 'Sweet Warm Date Night',
          triggers: { style: ['sweet'], occasion: ['date', 'going-out'], intensity: ['balanced', 'strong'], impression: ['warm'], notes: ['vanilla', 'amber'] },
          scentFamilies: ['sweet', 'warm', 'gourmand', 'amber'],
          notes: ['vanilla', 'sweet', 'amber', 'tonka', 'cherry'],
          occasions: ['date-night', 'going-out', 'evening', 'romantic'],
          strengths: ['balanced', 'strong'],
          impressions: ['warm', 'inviting'],
          avoid: ['sharp-citrus', 'powdery-only'],
          description: 'A sweet and warm scent with vanilla or amber notes, made to feel inviting, smooth, and noticeable for date nights.'
        },
        {
          id: 'woody-rich-bold',
          label: 'Woody Rich Bold',
          triggers: { style: ['woody'], occasion: ['date', 'going-out'], intensity: ['strong'], impression: ['confident'], notes: ['woods', 'amber'] },
          scentFamilies: ['woody', 'rich', 'oud', 'amber'],
          notes: ['woods', 'oud', 'amber', 'leather', 'tobacco'],
          occasions: ['date-night', 'going-out', 'night-out', 'statement'],
          strengths: ['strong'],
          impressions: ['confident', 'bold', 'expensive'],
          genderPreference: ['men', 'unisex'],
          avoid: ['soft-floral', 'light-citrus'],
          description: 'A woody and rich scent with oud, woods, or amber notes, made to feel bold, confident, and attention-grabbing.'
        },
        {
          id: 'floral-soft-elegant',
          label: 'Floral Soft Elegant',
          triggers: { style: ['floral'], occasion: ['everyday', 'gifting'], intensity: ['soft', 'balanced'], impression: ['polished', 'warm'], notes: ['powdery'] },
          scentFamilies: ['floral', 'soft', 'powdery'],
          notes: ['floral', 'powdery', 'rose', 'jasmine', 'iris'],
          occasions: ['everyday', 'gifting', 'daily'],
          strengths: ['soft', 'balanced'],
          impressions: ['polished', 'warm', 'elegant'],
          genderPreference: ['women', 'unisex'],
          avoid: ['heavy-oud', 'aggressive-spice', 'masculine-woody'],
          description: 'A soft floral scent with powdery notes, made to feel elegant, polished, and easy to wear.'
        },
        {
          id: 'spicy-bold-memorable',
          label: 'Spicy Bold Memorable',
          triggers: { style: ['spicy'], occasion: ['going-out', 'date'], intensity: ['strong'], impression: ['unique', 'confident'], notes: ['amber', 'woods'] },
          scentFamilies: ['spicy', 'bold', 'warm', 'amber'],
          notes: ['amber', 'warm', 'spicy', 'woods', 'tobacco'],
          occasions: ['going-out', 'date-night', 'night-out', 'statement'],
          strengths: ['strong'],
          impressions: ['unique', 'memorable', 'confident', 'bold'],
          avoid: ['soft-skin', 'basic-fresh'],
          description: 'A spicy and bold scent with warm amber or woody notes, made to stand out and feel unique.'
        },
        {
          id: 'luxury-polished',
          label: 'Luxury Polished',
          triggers: { style: ['woody', 'floral', 'sweet'], occasion: ['work', 'gifting', 'date'], intensity: ['balanced'], impression: ['polished'], notes: ['amber', 'powdery', 'musk'] },
          scentFamilies: ['polished', 'luxury', 'smooth', 'clean'],
          notes: ['amber', 'musk', 'floral', 'powdery', 'smooth'],
          occasions: ['work', 'gifting', 'date-night', 'office-safe'],
          strengths: ['balanced'],
          impressions: ['polished', 'expensive', 'elegant'],
          avoid: ['unrefined-spicy'],
          description: 'A polished and expensive-smelling scent with smooth notes, made to feel refined, elegant, and premium.'
        },
        {
          id: 'gift-safe',
          label: 'Gift Safe',
          triggers: { occasion: ['gifting'], intensity: ['balanced'], impression: ['clean', 'warm', 'polished'] },
          scentFamilies: ['clean', 'warm', 'smooth', 'crowd-pleaser'],
          notes: ['musk', 'amber', 'sweet', 'clean'],
          occasions: ['gifting', 'everyday'],
          strengths: ['balanced'],
          impressions: ['clean', 'warm', 'polished'],
          avoid: ['polarizing-oud', 'very-strong-spice', 'extreme-powder'],
          giftSafe: true,
          description: 'A balanced and easy-to-love scent that works well as a gift because it feels clean, smooth, and versatile.'
        }
      ];

      const productProfileMap = {
        'army-1': { gender: ['men', 'unisex'], scentFamilies: ['fresh', 'clean', 'citrus'], notes: ['citrus', 'fresh', 'musk'], occasions: ['everyday', 'work'], strengths: ['balanced'], impressions: ['clean', 'confident'], profileIds: ['fresh-clean-everyday', 'gift-safe'], giftSafe: true },
        'cristal': { gender: ['unisex'], scentFamilies: ['fresh', 'clean', 'amber', 'musk', 'polished'], notes: ['amber', 'musk', 'citrus', 'clean'], occasions: ['everyday', 'work', 'gifting', 'date-night'], strengths: ['balanced'], impressions: ['clean', 'polished', 'expensive'], profileIds: ['fresh-clean-everyday', 'luxury-polished', 'gift-safe'], bestSeller: true, giftSafe: true },
        'inspired-by-another-13': { gender: ['unisex'], scentFamilies: ['fresh', 'clean', 'musk', 'unique'], notes: ['musk', 'clean', 'fresh'], occasions: ['everyday', 'work', 'gifting'], strengths: ['soft', 'balanced'], impressions: ['clean', 'unique', 'polished'], profileIds: ['fresh-clean-everyday', 'gift-safe'], giftSafe: true },
        'grandiose-mountain': { gender: ['unisex'], scentFamilies: ['fresh', 'clean', 'citrus', 'green'], notes: ['citrus', 'fresh', 'musk'], occasions: ['everyday', 'work'], strengths: ['balanced'], impressions: ['clean', 'put-together'], profileIds: ['fresh-clean-everyday'], giftSafe: true },
        'aqua-di-viginia': { gender: ['unisex'], scentFamilies: ['fresh', 'citrus', 'clean'], notes: ['citrus', 'fresh'], occasions: ['everyday', 'work'], strengths: ['soft', 'balanced'], impressions: ['clean'], profileIds: ['fresh-clean-everyday'] },
        'porto-di-amore': { gender: ['unisex'], scentFamilies: ['fresh', 'citrus', 'clean'], notes: ['citrus', 'fresh', 'floral'], occasions: ['everyday', 'work', 'gifting'], strengths: ['soft', 'balanced'], impressions: ['clean', 'polished'], profileIds: ['fresh-clean-everyday', 'gift-safe'] },
        'emperor-tallool': { gender: ['unisex'], scentFamilies: ['fresh', 'citrus', 'marine', 'polished'], notes: ['citrus', 'fresh', 'musk'], occasions: ['everyday', 'work', 'gifting'], strengths: ['balanced'], impressions: ['clean', 'expensive'], profileIds: ['fresh-clean-everyday', 'luxury-polished', 'gift-safe'], giftSafe: true },
        'green-notes': { gender: ['men', 'unisex'], scentFamilies: ['fresh', 'green', 'clean', 'woody'], notes: ['fresh', 'citrus', 'woods'], occasions: ['everyday', 'work'], strengths: ['balanced'], impressions: ['clean', 'confident'], profileIds: ['fresh-clean-everyday', 'gift-safe'] },
        'waterfall': { gender: ['unisex'], scentFamilies: ['fresh', 'clean', 'woody'], notes: ['fresh', 'woods', 'musk'], occasions: ['everyday', 'work'], strengths: ['soft', 'balanced'], impressions: ['clean', 'unique'], profileIds: ['fresh-clean-everyday'] },
        'inspired-by-lv-afternoon-swim': { gender: ['unisex'], scentFamilies: ['fresh', 'citrus', 'clean'], notes: ['citrus', 'fresh'], occasions: ['everyday', 'work'], strengths: ['balanced'], impressions: ['clean', 'polished'], profileIds: ['fresh-clean-everyday'] },
        'inspired-by-lv-imagination': { gender: ['unisex'], scentFamilies: ['fresh', 'citrus', 'polished'], notes: ['citrus', 'fresh', 'musk'], occasions: ['everyday', 'work', 'gifting'], strengths: ['balanced'], impressions: ['clean', 'polished', 'expensive'], profileIds: ['fresh-clean-everyday', 'luxury-polished'] },
        'inspired-by-dg-light-bleu': { gender: ['women', 'unisex'], scentFamilies: ['fresh', 'clean', 'citrus'], notes: ['citrus', 'fresh'], occasions: ['everyday', 'work', 'gifting'], strengths: ['soft', 'balanced'], impressions: ['clean', 'polished'], profileIds: ['fresh-clean-everyday', 'gift-safe'], giftSafe: true },
        'inspired-by-aqua-di-gio-profumo': { gender: ['men', 'unisex'], scentFamilies: ['fresh', 'clean', 'marine', 'woody'], notes: ['citrus', 'fresh', 'woods'], occasions: ['everyday', 'work', 'going-out'], strengths: ['balanced', 'strong'], impressions: ['clean', 'confident'], profileIds: ['fresh-clean-everyday', 'woody-rich-bold'] },
        'bleu-de-nuit': { gender: ['men', 'unisex'], scentFamilies: ['fresh', 'woody', 'clean'], notes: ['citrus', 'fresh', 'woods'], occasions: ['everyday', 'work', 'going-out'], strengths: ['balanced', 'strong'], impressions: ['clean', 'confident'], profileIds: ['fresh-clean-everyday', 'woody-rich-bold', 'gift-safe'], giftSafe: true },
        'ma-cherie': { gender: ['women', 'unisex'], scentFamilies: ['sweet', 'warm', 'fruity'], notes: ['cherry', 'vanilla', 'sweet', 'warm'], occasions: ['date-night', 'going-out'], strengths: ['balanced', 'strong'], impressions: ['warm', 'inviting', 'unique'], profileIds: ['sweet-warm-date-night', 'gift-safe'], giftSafe: true },
        'all-my-love-for-you': { gender: ['women'], scentFamilies: ['sweet', 'warm', 'floral', 'gourmand'], notes: ['vanilla', 'sweet', 'floral'], occasions: ['date-night', 'gifting'], strengths: ['balanced'], impressions: ['warm', 'inviting'], profileIds: ['sweet-warm-date-night', 'floral-soft-elegant', 'gift-safe'], bestSeller: true, giftSafe: true },
        'cigare-vanille': { gender: ['unisex'], scentFamilies: ['sweet', 'warm', 'spicy'], notes: ['vanilla', 'tobacco', 'amber', 'sweet'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['warm', 'confident', 'bold'], profileIds: ['sweet-warm-date-night', 'spicy-bold-memorable'] },
        'inspired-by-vanilla-powder': { gender: ['unisex'], scentFamilies: ['sweet', 'warm', 'powdery'], notes: ['vanilla', 'sweet', 'powdery'], occasions: ['date-night', 'gifting', 'everyday'], strengths: ['balanced'], impressions: ['warm', 'polished'], profileIds: ['sweet-warm-date-night', 'luxury-polished'] },
        'inspired-by-tf-vanilla-sex': { gender: ['unisex'], scentFamilies: ['sweet', 'warm', 'gourmand'], notes: ['vanilla', 'sweet', 'warm'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['warm', 'unique'], profileIds: ['sweet-warm-date-night'] },
        'inspired-by-angles-share': { gender: ['unisex'], scentFamilies: ['sweet', 'warm', 'spicy', 'amber'], notes: ['amber', 'vanilla', 'sweet', 'warm'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['warm', 'unique', 'memorable'], profileIds: ['sweet-warm-date-night', 'spicy-bold-memorable'] },
        'inspired-by-bianco-latte': { gender: ['unisex'], scentFamilies: ['sweet', 'warm', 'gourmand'], notes: ['vanilla', 'sweet', 'warm'], occasions: ['date-night', 'gifting'], strengths: ['balanced'], impressions: ['warm', 'inviting'], profileIds: ['sweet-warm-date-night', 'gift-safe'], giftSafe: true },
        'blueberry': { gender: ['women', 'unisex'], scentFamilies: ['sweet', 'warm', 'fruity'], notes: ['vanilla', 'sweet', 'warm'], occasions: ['date-night', 'gifting', 'everyday'], strengths: ['balanced'], impressions: ['warm', 'inviting'], profileIds: ['sweet-warm-date-night', 'gift-safe'], giftSafe: true },
        'black-flower': { gender: ['women'], scentFamilies: ['sweet', 'warm', 'gourmand'], notes: ['vanilla', 'sweet', 'coffee'], occasions: ['date-night', 'going-out'], strengths: ['balanced', 'strong'], impressions: ['warm', 'inviting', 'unique'], profileIds: ['sweet-warm-date-night'] },
        'inspired-by-arabian-tonka-montale': { gender: ['unisex'], scentFamilies: ['sweet', 'warm', 'amber', 'spicy'], notes: ['tonka', 'amber', 'vanilla', 'warm'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['warm', 'unique', 'memorable'], profileIds: ['sweet-warm-date-night', 'spicy-bold-memorable'] },
        'inspired-by-black-phantom': { gender: ['unisex'], scentFamilies: ['sweet', 'warm', 'dark'], notes: ['vanilla', 'sweet', 'coffee'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['warm', 'unique', 'memorable'], profileIds: ['sweet-warm-date-night', 'spicy-bold-memorable'] },
        'woody-oudy': { gender: ['men', 'unisex'], scentFamilies: ['woody', 'rich', 'oud'], notes: ['woods', 'oud', 'amber'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['confident', 'bold', 'expensive'], profileIds: ['woody-rich-bold'] },
        'inspired-by-ameer-al-oudh': { gender: ['men', 'unisex'], scentFamilies: ['woody', 'rich', 'oud'], notes: ['oud', 'woods', 'amber'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['confident', 'bold', 'unique'], profileIds: ['woody-rich-bold', 'spicy-bold-memorable'] },
        'inspired-by-oud-satin-mood': { gender: ['unisex'], scentFamilies: ['woody', 'rich', 'oud', 'amber', 'polished'], notes: ['oud', 'amber', 'floral'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['polished', 'confident', 'expensive'], profileIds: ['woody-rich-bold', 'luxury-polished'] },
        'inspired-by-ombre-leather': { gender: ['men', 'unisex'], scentFamilies: ['woody', 'leather', 'rich'], notes: ['leather', 'woods', 'amber'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['confident', 'bold'], profileIds: ['woody-rich-bold'] },
        'toscano-code': { gender: ['men', 'unisex'], scentFamilies: ['woody', 'leather', 'spicy'], notes: ['leather', 'tobacco', 'woods'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['confident', 'bold', 'unique'], profileIds: ['woody-rich-bold', 'spicy-bold-memorable'] },
        'inspired-by-black-afgano': { gender: ['men', 'unisex'], scentFamilies: ['woody', 'oud', 'spicy', 'dark'], notes: ['woods', 'oud', 'amber'], occasions: ['going-out', 'night-out'], strengths: ['strong'], impressions: ['unique', 'memorable', 'bold'], profileIds: ['woody-rich-bold', 'spicy-bold-memorable'] },
        'inspired-by-amouagen-interlude': { gender: ['men', 'unisex'], scentFamilies: ['spicy', 'amber', 'woody'], notes: ['amber', 'spicy', 'woods'], occasions: ['going-out', 'date-night'], strengths: ['strong'], impressions: ['confident', 'unique', 'memorable'], profileIds: ['spicy-bold-memorable', 'woody-rich-bold'] },
        'inspired-by-the-fire-place': { gender: ['unisex'], scentFamilies: ['spicy', 'warm', 'woody'], notes: ['amber', 'woods', 'warm', 'spicy'], occasions: ['going-out', 'date-night'], strengths: ['strong'], impressions: ['warm', 'unique', 'memorable'], profileIds: ['spicy-bold-memorable'] },
        'inspired-by-t-f-bitter-peach': { gender: ['unisex'], scentFamilies: ['sweet', 'warm', 'spicy'], notes: ['sweet', 'amber', 'warm'], occasions: ['date-night', 'going-out'], strengths: ['balanced', 'strong'], impressions: ['unique', 'warm'], profileIds: ['sweet-warm-date-night', 'spicy-bold-memorable'] },
        'fantastic-midnight': { gender: ['unisex'], scentFamilies: ['spicy', 'bold', 'warm'], notes: ['amber', 'spicy', 'woods'], occasions: ['going-out', 'date-night'], strengths: ['strong'], impressions: ['confident', 'unique', 'memorable'], profileIds: ['spicy-bold-memorable'] },
        'inspired-by-jazz-club': { gender: ['men', 'unisex'], scentFamilies: ['spicy', 'warm', 'woody'], notes: ['tobacco', 'amber', 'warm', 'woods'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['warm', 'unique', 'confident'], profileIds: ['spicy-bold-memorable', 'sweet-warm-date-night'] },
        'heart-code': { gender: ['men', 'unisex'], scentFamilies: ['warm', 'spicy', 'amber'], notes: ['amber', 'warm', 'spicy'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['confident', 'warm'], profileIds: ['spicy-bold-memorable', 'sweet-warm-date-night'] },
        'i-am-the-boss': { gender: ['men', 'unisex'], scentFamilies: ['warm', 'spicy', 'polished'], notes: ['amber', 'warm', 'spicy'], occasions: ['date-night', 'going-out', 'work'], strengths: ['balanced', 'strong'], impressions: ['confident', 'polished'], profileIds: ['spicy-bold-memorable', 'luxury-polished'] },
        'inspired-by-delina': { gender: ['women'], scentFamilies: ['floral', 'soft', 'powdery'], notes: ['floral', 'powdery', 'rose'], occasions: ['everyday', 'gifting', 'date-night'], strengths: ['soft', 'balanced'], impressions: ['polished', 'warm', 'elegant'], profileIds: ['floral-soft-elegant', 'luxury-polished'], giftSafe: true },
        'aventuriere': { gender: ['women'], scentFamilies: ['floral', 'fresh', 'soft'], notes: ['floral', 'fresh', 'citrus'], occasions: ['everyday', 'gifting'], strengths: ['balanced'], impressions: ['clean', 'polished'], profileIds: ['floral-soft-elegant', 'gift-safe'], giftSafe: true },
        'inspired-by-creed-carmina': { gender: ['women'], scentFamilies: ['floral', 'soft', 'polished'], notes: ['floral', 'powdery', 'rose'], occasions: ['everyday', 'gifting', 'date-night'], strengths: ['balanced'], impressions: ['polished', 'expensive'], profileIds: ['floral-soft-elegant', 'luxury-polished'] },
        'inspired-by-flowerbomb-v-r': { gender: ['women'], scentFamilies: ['floral', 'sweet', 'soft'], notes: ['floral', 'powdery', 'sweet'], occasions: ['everyday', 'date-night'], strengths: ['balanced'], impressions: ['warm', 'polished'], profileIds: ['floral-soft-elegant', 'sweet-warm-date-night'] },
        'very-secret': { gender: ['women'], scentFamilies: ['floral', 'soft', 'clean'], notes: ['floral', 'powdery'], occasions: ['everyday', 'gifting', 'work'], strengths: ['soft', 'balanced'], impressions: ['polished', 'clean'], profileIds: ['floral-soft-elegant', 'gift-safe'], giftSafe: true },
        'hello-coco': { gender: ['women'], scentFamilies: ['floral', 'sweet', 'polished'], notes: ['floral', 'sweet', 'powdery'], occasions: ['everyday', 'date-night', 'gifting'], strengths: ['balanced'], impressions: ['polished', 'warm'], profileIds: ['floral-soft-elegant', 'luxury-polished', 'gift-safe'], giftSafe: true },
        'mia-flora': { gender: ['women'], scentFamilies: ['floral', 'soft', 'fresh'], notes: ['floral', 'powdery', 'fresh'], occasions: ['everyday', 'gifting'], strengths: ['soft', 'balanced'], impressions: ['polished', 'warm'], profileIds: ['floral-soft-elegant', 'gift-safe'], giftSafe: true },
        'inspired-by-gucci-bloom': { gender: ['women'], scentFamilies: ['floral', 'soft'], notes: ['floral', 'jasmine', 'powdery'], occasions: ['everyday', 'gifting'], strengths: ['soft', 'balanced'], impressions: ['warm', 'polished'], profileIds: ['floral-soft-elegant', 'gift-safe'], giftSafe: true },
        'inspired-by-gucci-bamboo': { gender: ['women'], scentFamilies: ['floral', 'soft', 'polished'], notes: ['floral', 'powdery'], occasions: ['everyday', 'work', 'gifting'], strengths: ['balanced'], impressions: ['polished', 'clean'], profileIds: ['floral-soft-elegant', 'luxury-polished'] },
        'tres-adorable': { gender: ['women'], scentFamilies: ['floral', 'soft', 'polished'], notes: ['floral', 'powdery'], occasions: ['everyday', 'gifting'], strengths: ['soft', 'balanced'], impressions: ['polished', 'warm'], profileIds: ['floral-soft-elegant', 'gift-safe'], giftSafe: true },
        'orchidee': { gender: ['women', 'unisex'], scentFamilies: ['floral', 'rich', 'spicy'], notes: ['floral', 'amber', 'spicy'], occasions: ['date-night', 'going-out'], strengths: ['strong'], impressions: ['unique', 'polished'], profileIds: ['spicy-bold-memorable', 'luxury-polished'] },
        'inspired-by-lv-matiere-noire': { gender: ['women', 'unisex'], scentFamilies: ['floral', 'woody', 'polished'], notes: ['floral', 'woods', 'amber'], occasions: ['date-night', 'gifting'], strengths: ['balanced'], impressions: ['polished', 'expensive'], profileIds: ['floral-soft-elegant', 'luxury-polished'] },
        'sandal-2020': { gender: ['unisex'], scentFamilies: ['woody', 'clean', 'polished'], notes: ['woods', 'musk', 'clean'], occasions: ['everyday', 'work', 'gifting'], strengths: ['balanced'], impressions: ['polished', 'clean', 'expensive'], profileIds: ['luxury-polished', 'fresh-clean-everyday', 'gift-safe'], giftSafe: true },
        'aventurier': { gender: ['men', 'unisex'], scentFamilies: ['woody', 'fresh', 'clean'], notes: ['woods', 'citrus', 'fresh'], occasions: ['everyday', 'work', 'going-out', 'gifting'], strengths: ['balanced', 'strong'], impressions: ['confident', 'clean'], profileIds: ['woody-rich-bold', 'gift-safe'], bestSeller: true, giftSafe: true },
        'inspired-by-aventus-absolu': { gender: ['men', 'unisex'], scentFamilies: ['woody', 'fresh', 'rich'], notes: ['woods', 'citrus', 'amber'], occasions: ['going-out', 'date-night'], strengths: ['strong'], impressions: ['confident', 'bold'], profileIds: ['woody-rich-bold'] },
        'inspired-by-nishane-hacivat': { gender: ['men', 'unisex'], scentFamilies: ['woody', 'fresh', 'polished'], notes: ['woods', 'citrus', 'fresh'], occasions: ['work', 'going-out'], strengths: ['balanced', 'strong'], impressions: ['confident', 'polished'], profileIds: ['woody-rich-bold', 'luxury-polished'] },
        'inspired-by-alexandria-ii-xerjoff': { gender: ['unisex'], scentFamilies: ['woody', 'rich', 'polished'], notes: ['woods', 'amber', 'powdery'], occasions: ['date-night', 'gifting'], strengths: ['balanced', 'strong'], impressions: ['polished', 'expensive'], profileIds: ['luxury-polished', 'woody-rich-bold'] }
      };

      const toArray = (value) => Array.isArray(value) ? value : value ? [value] : [];
      const hasAny = (source, wanted) => toArray(wanted).some((item) => toArray(source).includes(item));
      const unique = (items) => [...new Set(toArray(items).filter(Boolean))];
      const normalizeProductHandle = (product) => {
        const explicitHandle = String(product.handle || '').trim();
        if (explicitHandle) return explicitHandle.toLowerCase();
        const fromUrl = String(product.url || '').split('/products/')[1] || '';
        return fromUrl.split(/[?#/]/)[0].toLowerCase();
      };
      const formatAttribute = (value) => String(value || '').replace(/-/g, ' ');

      const inferProductProfile = (product) => {
        const haystack = haystackFor(product);
        const inferred = {
          gender: ['unisex'],
          scentFamilies: [],
          notes: [],
          occasions: [],
          strengths: ['balanced'],
          impressions: [],
          profileIds: [],
          bestSeller: Number(product.index || 0) > 0 && Number(product.index || 0) <= 12,
          giftSafe: Number(product.index || 0) > 0 && Number(product.index || 0) <= 12
        };

        if (/women|feminine|for her|delina|flower|carmina/.test(haystack)) inferred.gender = ['women'];
        if (/men|masculine|for him|aventus|leather|oud|tobacco/.test(haystack)) inferred.gender = inferred.gender.includes('women') ? ['unisex'] : ['men'];
        if (/fresh|clean|citrus|bergamot|lemon|lime|musk|aquatic|marine/.test(haystack)) {
          inferred.scentFamilies.push('fresh', 'clean');
          inferred.notes.push('fresh', 'citrus', 'musk');
          inferred.occasions.push('everyday', 'work');
          inferred.impressions.push('clean');
        }
        if (/sweet|vanilla|cherry|tonka|gourmand|caramel/.test(haystack)) {
          inferred.scentFamilies.push('sweet', 'warm');
          inferred.notes.push('vanilla', 'sweet');
          inferred.occasions.push('date-night');
          inferred.impressions.push('warm');
        }
        if (/wood|woody|oud|cedar|sandal|leather|tobacco/.test(haystack)) {
          inferred.scentFamilies.push('woody', 'rich');
          inferred.notes.push('woods', 'oud');
          inferred.occasions.push('going-out', 'date-night');
          inferred.impressions.push('confident');
        }
        if (/floral|flower|rose|jasmine|powder|powdery|iris/.test(haystack)) {
          inferred.scentFamilies.push('floral', 'soft');
          inferred.notes.push('floral', 'powdery');
          inferred.occasions.push('everyday', 'gifting');
          inferred.impressions.push('polished');
        }
        if (/spicy|spice|amber|incense|bold|intense/.test(haystack)) {
          inferred.scentFamilies.push('spicy', 'warm', 'amber');
          inferred.notes.push('amber', 'spicy');
          inferred.occasions.push('going-out', 'date-night');
          inferred.strengths.push('strong');
          inferred.impressions.push('unique', 'bold');
        }

        Object.keys(inferred).forEach((key) => {
          if (Array.isArray(inferred[key])) inferred[key] = unique(inferred[key]);
        });
        return inferred;
      };

      const profileForProduct = (product) => {
        const manual = productProfileMap[normalizeProductHandle(product)];
        const inferred = inferProductProfile(product);
        const merged = manual ? { ...inferred, ...manual } : inferred;
        ['gender', 'scentFamilies', 'notes', 'occasions', 'strengths', 'impressions', 'profileIds', 'avoid'].forEach((key) => {
          merged[key] = unique(merged[key]);
        });
        merged.manualProfile = Boolean(manual);
        merged.bestSeller = Boolean(merged.bestSeller || inferred.bestSeller);
        merged.giftSafe = Boolean(merged.giftSafe || inferred.giftSafe);
        return merged;
      };

      const scoreTrigger = (profile, selectedAnswers) => {
        let score = 0;
        const triggers = profile.triggers || {};
        if (triggers.style && triggers.style.includes(selectedAnswers.style)) score += 5;
        if (triggers.notes && triggers.notes.includes(selectedAnswers.notes)) score += 5;
        if (triggers.occasion && triggers.occasion.includes(selectedAnswers.occasion)) score += 4;
        if (triggers.intensity && triggers.intensity.includes(selectedAnswers.intensity)) score += 3;
        if (triggers.impression && triggers.impression.includes(selectedAnswers.impression)) score += 4;
        if (selectedAnswers.email === 'submitted' && profile.giftSafe) score += 1;
        return score;
      };

      const activeAnswers = () => ({ ...answers, email: answers.email || emailState.status || 'none' });

      const winningProfileFor = (selectedAnswers = activeAnswers()) => {
        return quizProfiles
          .map((profile) => ({ ...profile, triggerScore: scoreTrigger(profile, selectedAnswers) }))
          .sort((a, b) => b.triggerScore - a.triggerScore || quizProfiles.findIndex((item) => item.id === a.id) - quizProfiles.findIndex((item) => item.id === b.id))[0];
      };

      const scoreProduct = (product, activeProfile = winningProfileFor()) => {
        const selectedAnswers = activeAnswers();
        const profile = profileForProduct(product);
        const parts = {
          profile: hasAny(profile.profileIds, activeProfile.id) ? 4 : 0,
          style: hasAny(profile.scentFamilies, answerAttributes.style[selectedAnswers.style]) ? 5 : 0,
          notes: hasAny(profile.notes, answerAttributes.notes[selectedAnswers.notes]) ? 5 : 0,
          occasion: hasAny(profile.occasions, answerAttributes.occasion[selectedAnswers.occasion]) ? 4 : 0,
          strength: hasAny(profile.strengths, answerAttributes.intensity[selectedAnswers.intensity]) ? 3 : 0,
          impression: hasAny(profile.impressions, answerAttributes.impression[selectedAnswers.impression]) ? 4 : 0,
          gender: activeProfile.genderPreference ? (hasAny(profile.gender, activeProfile.genderPreference) ? 5 : -12) : 0,
          giftSafe: selectedAnswers.occasion === 'gifting' && (profile.bestSeller || profile.giftSafe) ? 2 : 0,
          emailStep: selectedAnswers.email === 'submitted' && (profile.bestSeller || profile.giftSafe) ? 1 : 0,
          avoid: hasAny(profile.scentFamilies.concat(profile.notes, profile.impressions), activeProfile.avoid) ? -8 : 0
        };
        const rawScore = Object.values(parts).reduce((sum, value) => sum + value, 0);
        const score = profile.manualProfile ? rawScore : Math.round(rawScore * 0.55);
        return { score, parts, productProfile: profile };
      };

      const clearFutureAnswers = (currentIndex) => {
        steps.slice(currentIndex + 1).forEach((step) => {
          if (step.hasAttribute('data-quiz-email-step')) return;
          const key = step.dataset.quizStep;
          if (key) delete answers[key];
          qsa('[data-quiz-option]', step).forEach((node) => {
            node.classList.remove('is-selected');
            node.setAttribute('aria-pressed', 'false');
          });
        });
      };

      const buildReason = (product, activeProfile = winningProfileFor()) => {
        const selectedAnswers = activeAnswers();
        const productProfile = profileForProduct(product);
        const selectedOccasion = labelFor('occasion', selectedAnswers.occasion).toLowerCase();
        const selectedIntensity = labelFor('intensity', selectedAnswers.intensity).toLowerCase();
        const selectedImpression = labelFor('impression', selectedAnswers.impression).toLowerCase();
        const selectedNotes = labelFor('notes', selectedAnswers.notes).toLowerCase();
        const preferredNotes = unique(
          productProfile.notes
            .filter((note) => hasAny(activeProfile.notes, note) || hasAny(answerAttributes.notes[selectedAnswers.notes], note))
        ).slice(0, 2);
        const notePhrase = preferredNotes.length ? preferredNotes.map(formatAttribute).join(' and ') : selectedNotes;
        const occasionPhrase = selectedOccasion || 'your plans';
        const intensityPhrase = selectedIntensity || 'your strength';
        const impressionPhrase = selectedImpression || 'your chosen impression';

        return `${activeProfile.description} This match leans ${notePhrase}, fits a ${intensityPhrase} preference, and works for ${occasionPhrase} when you want to feel ${impressionPhrase}.`;
      };

      const rankedProductsFor = () => {
        const selectedAnswers = activeAnswers();
        const activeProfile = winningProfileFor(selectedAnswers);
        const scored = products
          .filter((product) => product && product.variantId)
          .map((product) => {
            const result = scoreProduct(product, activeProfile);
            return {
              ...product,
              score: result.score,
              scoreParts: result.parts,
              productProfile: result.productProfile
            };
          })
          .sort((a, b) => b.score - a.score || (a.index || 0) - (b.index || 0));

        const seen = new Set();
        let ranked = scored
          .filter((product) => product.score > 0)
          .filter((product) => {
            const key = normalizeProductHandle(product) || String(product.variantId || '');
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });

        if (!ranked.length) {
          const fallbackProfile = quizProfiles.find((profile) => profile.id === 'gift-safe') || activeProfile;
          ranked = products
            .filter((product) => product && product.variantId)
            .map((product) => {
              const productProfile = profileForProduct(product);
              const result = scoreProduct(product, fallbackProfile);
              return {
                ...product,
                score: Math.max(result.score, productProfile.bestSeller || productProfile.giftSafe ? 1 : 0),
                scoreParts: result.parts,
                productProfile
              };
            })
            .filter((product) => product.score > 0)
            .sort((a, b) => b.score - a.score || (a.index || 0) - (b.index || 0));
        }

        ranked = ranked.slice(0, 3);
        const descriptions = ranked.map((product) => buildReason(product, activeProfile));
        lastQuizResult = {
          selectedAnswers,
          winningProfile: {
            id: activeProfile.id,
            label: activeProfile.label,
            triggerScore: activeProfile.triggerScore
          },
          finalScoreObject: scored.slice(0, 10).map((product) => ({
            handle: normalizeProductHandle(product),
            title: product.displayTitle || cleanProductTitle(product.title),
            score: product.score,
            parts: product.scoreParts
          })),
          finalRecommendedProductHandles: ranked.map((product) => normalizeProductHandle(product)),
          visibleResultCardDescriptions: descriptions
        };

        return { ranked, descriptions, activeProfile };
      };

      const updateEmailResult = () => {
        if (!emailResult) return;
        if (emailState.status === 'submitted') {
          emailResult.hidden = false;
          emailResult.textContent = "Your matches are ready. We'll also send them to your inbox.";
        } else if (emailState.status === 'failed') {
          emailResult.hidden = false;
          emailResult.textContent = "Your matches are ready. We couldn't save your email this time.";
        } else {
          emailResult.hidden = true;
          emailResult.textContent = '';
        }
      };

      const showResults = () => {
        if (!answers.email) answers.email = emailState.status || 'none';
        steps.forEach((step) => step.classList.remove('is-active'));
        if (progress) progress.style.width = '100%';
        if (progressText) progressText.textContent = 'Results ready';
        quiz.classList.add('has-results');
        updateEmailResult();
        const result = rankedProductsFor();
        const ranked = result.ranked;

        if (resultGrid) {
          resultGrid.innerHTML = ranked.length ? ranked.map((product, productIndex) => `
            <article class="quiz-result-card" data-score="${escapeHtml(product.score || 0)}" data-profile="${escapeHtml(result.activeProfile.id)}" data-handle="${escapeHtml(normalizeProductHandle(product))}">
              <div class="quiz-result-card__media">
                ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.displayTitle || cleanProductTitle(product.title))}">` : ''}
              </div>
              <div class="quiz-result-card__body">
                <h3>${escapeHtml(product.displayTitle || cleanProductTitle(product.title))}</h3>
                ${product.inspiredBy ? `<p class="quiz-result-card__inspired">Inspired by ${escapeHtml(product.inspiredBy)}</p>` : ''}
                <p class="quiz-result-card__reason">${escapeHtml(result.descriptions[productIndex] || buildReason(product, result.activeProfile))}</p>
                <div class="quiz-result-card__price">${product.priceHtml || escapeHtml(product.price)}</div>
                <form method="post" action="/cart/add" class="js-product-form quiz-result-card__form">
                  <input type="hidden" name="id" value="${escapeHtml(product.variantId)}">
                  <button class="button button--primary button--full" type="submit">Add to Cart</button>
                </form>
                <a class="text-button quiz-result-card__link" href="${escapeHtml(product.url)}">View Product</a>
              </div>
            </article>
          `).join('') : `
            <div class="quiz-empty">
              <h3>No available matches found right now.</h3>
              <p>Explore all scents or build a bundle while we restock more recommendations.</p>
              <a class="button button--secondary" href="/collections/all?sort_by=best-selling">Explore All Scents</a>
            </div>
          `;
        }
        if (results) results.classList.add('is-active');
      };

      const setDebugAnswers = (nextAnswers = {}) => {
        Object.keys(answers).forEach((key) => delete answers[key]);
        Object.assign(answers, nextAnswers);
        if (!answers.email) answers.email = 'skipped';
        emailState.status = answers.email;
        qsa('[data-quiz-option]', quiz).forEach((node) => {
          const step = node.closest('[data-quiz-step]');
          const selected = Boolean(step && answers[step.dataset.quizStep] === node.dataset.value);
          node.classList.toggle('is-selected', selected);
          node.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      };

      const runDebugPath = (pathAnswers = {}, options = {}) => {
        setDebugAnswers(pathAnswers);
        const result = rankedProductsFor();
        if (options.render) showResults();
        return {
          selectedAnswers: { ...lastQuizResult.selectedAnswers },
          winningProfile: { ...lastQuizResult.winningProfile },
          finalScoreObject: lastQuizResult.finalScoreObject.map((product) => ({
            handle: product.handle,
            title: product.title,
            score: product.score,
            parts: { ...product.parts }
          })),
          finalRecommendedProductHandles: [...lastQuizResult.finalRecommendedProductHandles],
          visibleResultCardDescriptions: [...lastQuizResult.visibleResultCardDescriptions],
          rendered: Boolean(options.render),
          resultCount: result.ranked.length
        };
      };

      window.__ipQuizDebug = window.__ipQuizDebug || {};
      window.__ipQuizDebug.runPath = runDebugPath;
      window.__ipQuizDebug.getLastResult = () => lastQuizResult ? {
        selectedAnswers: { ...lastQuizResult.selectedAnswers },
        winningProfile: { ...lastQuizResult.winningProfile },
        finalScoreObject: lastQuizResult.finalScoreObject.map((product) => ({
          handle: product.handle,
          title: product.title,
          score: product.score,
          parts: { ...product.parts }
        })),
        finalRecommendedProductHandles: [...lastQuizResult.finalRecommendedProductHandles],
        visibleResultCardDescriptions: [...lastQuizResult.visibleResultCardDescriptions]
      } : null;
      window.__ipQuizDebug.getAnswers = () => activeAnswers();

      const setEmailMessage = (message, type) => {
        if (!emailMessage) return;
        emailMessage.textContent = message || '';
        emailMessage.classList.toggle('is-error', type === 'error');
        emailMessage.classList.toggle('is-success', type === 'success');
      };

      const submitEmail = async () => {
        if (!emailForm || !emailInput) {
          emailState.status = 'failed';
          answers.email = emailState.status;
          showStep(Math.min(index + 1, steps.length - 1));
          return;
        }
        const email = emailInput.value.trim();
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!isValidEmail) {
          setEmailMessage('Please enter a valid email.', 'error');
          emailInput.focus();
          return;
        }

        const originalLabel = emailSubmit ? emailSubmit.textContent : '';
        if (emailSubmit) {
          emailSubmit.disabled = true;
          emailSubmit.textContent = 'Saving...';
        }
        setEmailMessage('', '');

        try {
          const formData = new FormData(emailForm);
          const controller = window.AbortController ? new AbortController() : null;
          const timeout = controller ? window.setTimeout(() => controller.abort(), 2600) : null;
          const response = await fetch(emailForm.action || '/contact', {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: controller ? controller.signal : undefined
          });
          if (timeout) window.clearTimeout(timeout);
          emailState.status = response.ok ? 'submitted' : 'failed';
          emailState.value = email;
          answers.email = emailState.status;
          if (!response.ok) setEmailMessage("We couldn't save your email, but your quiz will continue.", 'error');
        } catch (error) {
          emailState.status = 'failed';
          emailState.value = email;
          answers.email = emailState.status;
          setEmailMessage("We couldn't save your email, but your quiz will continue.", 'error');
        } finally {
          if (emailSubmit) {
            emailSubmit.disabled = false;
            emailSubmit.textContent = originalLabel;
          }
        }

        window.setTimeout(() => showStep(Math.min(index + 1, steps.length - 1)), emailState.status === 'failed' ? 700 : 80);
      };

      quiz.addEventListener('click', (event) => {
        const option = event.target.closest('[data-quiz-option]');
        const retake = event.target.closest('[data-quiz-retake]');
        const back = event.target.closest('[data-quiz-back]');
        const skipEmail = event.target.closest('[data-quiz-skip-email]');
        if (back) {
          showStep(Math.max(0, index - 1));
          return;
        }
        if (skipEmail) {
          emailState.status = 'skipped';
          answers.email = emailState.status;
          setEmailMessage('', '');
          showStep(Math.min(index + 1, steps.length - 1));
          return;
        }
        if (retake) {
          Object.keys(answers).forEach((key) => delete answers[key]);
          emailState.status = 'none';
          emailState.value = '';
          lastQuizResult = null;
          if (emailInput) emailInput.value = '';
          setEmailMessage('', '');
          qsa('[data-quiz-option]', quiz).forEach((node) => {
            node.classList.remove('is-selected');
            node.setAttribute('aria-pressed', 'false');
          });
          showStep(0);
          return;
        }
        if (!option) return;
        const step = option.closest('[data-quiz-step]');
        const key = step.dataset.quizStep;
        answers[key] = option.dataset.value;
        clearFutureAnswers(index);
        qsa('[data-quiz-option]', step).forEach((node) => {
          const selected = node === option;
          node.classList.toggle('is-selected', selected);
          node.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        if (index + 1 < steps.length) {
          window.setTimeout(() => showStep(index + 1), 140);
        } else {
          window.setTimeout(showResults, 140);
        }
      });

      if (emailForm) {
        emailForm.addEventListener('submit', (event) => {
          event.preventDefault();
          submitEmail();
        });
      }

      qsa('[data-quiz-option]', quiz).forEach((node) => {
        if (!node.hasAttribute('aria-pressed')) node.setAttribute('aria-pressed', 'false');
      });

      showStep(0);
    });
  }

  function initTabbedProducts() {
    qsa('[data-ip-tabs]').forEach((section) => {
      const buttons = qsa('[data-tab-button]', section);
      const panels = qsa('[data-tab-panel]', section);
      const controls = qsa('[data-carousel-prev], [data-carousel-next]', section);

      const activePanel = () => panels.find((panel) => panel.classList.contains('is-active')) || panels[0];

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const targetId = button.dataset.tabTarget;
          buttons.forEach((item) => {
            const isActive = item === button;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });
          panels.forEach((panel) => {
            const isActive = panel.id === targetId;
            panel.classList.toggle('is-active', isActive);
            panel.hidden = !isActive;
          });
        });
      });

      controls.forEach((control) => {
        control.addEventListener('click', () => {
          const panel = activePanel();
          const track = qs('[data-carousel-track]', panel);
          if (!track) return;
          const direction = control.matches('[data-carousel-prev]') ? -1 : 1;
          const amount = Math.max(track.clientWidth * 0.78, 260);
          track.scrollBy({ left: amount * direction, behavior: 'smooth' });
        });
      });
    });
  }

  function initReviewCarousel() {
    qsa('[data-ip-review-carousel]').forEach((carousel) => {
      const track = qs('[data-ip-review-track]', carousel);
      const slides = track ? qsa('[data-ip-review-slide]', track) : [];
      const dots = qsa('[data-ip-review-dot]', carousel);
      const previous = qs('[data-ip-review-prev]', carousel);
      const next = qs('[data-ip-review-next]', carousel);
      if (!track || slides.length < 2) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let activeIndex = 0;
      let scrollFrame = 0;
      let resizeFrame = 0;

      const closestIndex = () => {
        const scrollLeft = track.scrollLeft;
        return slides.reduce((closest, slide, index) => {
          const distance = Math.abs(slide.offsetLeft - scrollLeft);
          return distance < closest.distance ? { index, distance } : closest;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
      };

      const updateState = (nextIndex = closestIndex()) => {
        activeIndex = Math.max(0, Math.min(slides.length - 1, nextIndex));
        slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
      };

      const scrollToIndex = (index, immediate = false) => {
        const normalizedIndex = (index + slides.length) % slides.length;
        const slide = slides[normalizedIndex];
        track.scrollTo({
          left: Math.max(0, slide.offsetLeft),
          behavior: immediate || reducedMotion.matches ? 'auto' : 'smooth'
        });
        updateState(normalizedIndex);
      };

      const scheduleUpdate = () => {
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          updateState();
        });
      };

      previous && previous.addEventListener('click', () => scrollToIndex(activeIndex - 1));
      next && next.addEventListener('click', () => scrollToIndex(activeIndex + 1));

      dots.forEach((dot) => {
        dot.addEventListener('click', () => scrollToIndex(Number(dot.dataset.reviewIndex || 0)));
      });

      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollToIndex(activeIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollToIndex(activeIndex + 1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          scrollToIndex(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          scrollToIndex(slides.length - 1);
        }
      });

      track.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.addEventListener('resize', () => {
        if (resizeFrame) return;
        resizeFrame = window.requestAnimationFrame(() => {
          resizeFrame = 0;
          scrollToIndex(activeIndex, true);
        });
      });

      window.setTimeout(() => scrollToIndex(0, true), 40);
      updateState(0);
    });
  }

  function initCuratedMobileDrawer() {
    const drawerMenus = qsa('.menu-drawer__navigation > .menu-drawer__menu');
    if (!drawerMenus.length) return;

    const curatedMarkup = `
      <li class="ip-mobile-menu-offer">
        <span>Bundle & save: 15% off 3 sprays, 20% off 5</span>
      </li>
      <li>
        <details class="ip-mobile-menu-group" open>
          <summary class="menu-drawer__menu-item list-menu__item link link--text focus-inset">
            Shop
            <span class="ip-mobile-menu-caret" aria-hidden="true">+</span>
          </summary>
          <ul class="ip-mobile-submenu" role="list">
            <li><a href="/collections/fragrances-for-him">Men's Fragrances</a></li>
            <li><a href="/collections/fragrances-for-women">Women's Fragrances</a></li>
            <li><a href="/collections/sample-discovery-sets">Discovery Sets</a></li>
            <li><a href="/collections/fragrance-oils-1">Fragrance Oils For Her</a></li>
            <li><a href="/collections/fragrance-oils">Fragrance Oils For Him</a></li>
            <li><a href="/collections/all?sort_by=best-selling">Best Sellers</a></li>
          </ul>
        </details>
      </li>
      <li>
        <details class="ip-mobile-menu-group">
          <summary class="menu-drawer__menu-item list-menu__item link link--text focus-inset">
            Discover
            <span class="ip-mobile-menu-caret" aria-hidden="true">+</span>
          </summary>
          <ul class="ip-mobile-submenu" role="list">
            <li><a href="/collections/all?sort_by=best-selling&view=bundle-v15">Build a Bundle</a></li>
            <li><a href="/collections/all?view=scent-quiz-v2">Take the Scent Quiz</a></li>
            <li><a href="/pages/about-us">Our Story</a></li>
          </ul>
        </details>
      </li>
      <li>
        <details class="ip-mobile-menu-group">
          <summary class="menu-drawer__menu-item list-menu__item link link--text focus-inset">
            Help
            <span class="ip-mobile-menu-caret" aria-hidden="true">+</span>
          </summary>
          <ul class="ip-mobile-submenu" role="list">
            <li><a href="/pages/faq">FAQ</a></li>
            <li><a href="/pages/contact-us">Contact</a></li>
            <li><a href="/policies/shipping-policy">Shipping</a></li>
            <li><a href="/policies/refund-policy">Returns</a></li>
          </ul>
        </details>
      </li>
    `;

    drawerMenus.forEach((menu) => {
      if (qs('.ip-mobile-menu-offer', menu)) return;
      menu.classList.add('ip-mobile-nav');
      menu.innerHTML = curatedMarkup;
    });
  }

  function initMobileProductStickyAtc() {
    if (!document.body.classList.contains('template-product')) return;
    const stickyBar = qs('.ip-sticky-atc');
    if (!stickyBar || !stickyBar.parentElement) return;

    const anchor = document.createComment('ip-sticky-atc-anchor');
    const originalParent = stickyBar.parentElement;
    originalParent.insertBefore(anchor, stickyBar);

    const syncStickyParent = () => {
      if (window.matchMedia('(max-width: 749px)').matches) {
        if (stickyBar.parentElement !== document.body) document.body.appendChild(stickyBar);
        return;
      }

      if (stickyBar.parentElement === document.body && anchor.parentNode) {
        anchor.parentNode.insertBefore(stickyBar, anchor.nextSibling);
      }
    };

    syncStickyParent();
    window.addEventListener('resize', syncStickyParent);
  }

  function initLuxuryMotion() {
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotionQuery.matches) {
      document.documentElement.classList.add('ip-reduce-motion');
      return;
    }

    const revealSelectors = [
      '.ip-campaign-hero .hero__content > *',
      '.ip-campaign-hero .hero__visual',
      '.section-header > *',
      '.collection-hero__inner > *',
      '.product-card-wrapper',
      '.card-wrapper',
      '.collection-card',
      '.scent-card',
      '.review-card',
      '.trust-item',
      '.why-card',
      '.bundle-product-card',
      '.bundle-summary',
      '.bundle-steps > *',
      '.comparison-table',
      '.comparison-card',
      '.brand-story__content > *',
      '.brand-story__product',
      '.ip-email-capture .email-capture > *',
      '.product__media-item',
      '.product__info-container > *',
      '.ip-footer-links__brand',
      '.ip-footer-links__column'
    ];

    const revealSelector = revealSelectors.join(',');
    const preparedRevealTargets = new Set();
    let revealObserver = null;

    const isRevealCandidate = (element) => element &&
      element.nodeType === 1 &&
      !element.closest('.drawer, .menu-drawer, .modal, .predictive-search, .mobile-facets, script, style');

    const getRevealCandidates = (rootScope = document) => {
      if (!rootScope || !rootScope.querySelectorAll) return [];

      const candidates = [];
      if (rootScope.nodeType === 1 && rootScope.matches && rootScope.matches(revealSelector)) {
        candidates.push(rootScope);
      }

      candidates.push(...qsa(revealSelector, rootScope));
      return candidates.filter(isRevealCandidate);
    };

    const visibleNow = (element) => {
      const rect = element.getBoundingClientRect();
      return rect.top < window.innerHeight * .96 && rect.bottom > 0;
    };

    const reveal = (element) => {
      element.classList.add('is-visible');
    };

    document.documentElement.classList.add('ip-motion-ready');

    const prepareRevealTargets = (rootScope = document) => {
      getRevealCandidates(rootScope).forEach((element) => {
        if (preparedRevealTargets.has(element)) return;

        preparedRevealTargets.add(element);
        element.dataset.ipMotion = 'reveal';
        element.style.setProperty('--ip-reveal-index', String(Math.min((preparedRevealTargets.size - 1) % 8, 7)));

        if (!revealObserver || visibleNow(element)) {
          window.requestAnimationFrame(() => reveal(element));
          return;
        }

        revealObserver.observe(element);
      });
    };

    if ('IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: .12 });
    }

    prepareRevealTargets(document);

    if (document.body) {
      let revealMutationFrame = 0;
      const revealMutationRoots = new Set();
      const scheduleRevealRefresh = (rootScope = document) => {
        revealMutationRoots.add(rootScope);
        if (revealMutationFrame) return;
        revealMutationFrame = window.requestAnimationFrame(() => {
          revealMutationFrame = 0;
          revealMutationRoots.forEach((refreshRoot) => prepareRevealTargets(refreshRoot));
          revealMutationRoots.clear();
        });
      };

      const revealMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) scheduleRevealRefresh(node);
          });
        });
      });
      revealMutationObserver.observe(document.body, { childList: true, subtree: true });
      document.addEventListener('shopify:section:load', (event) => scheduleRevealRefresh(event.target));
      document.addEventListener('shopify:section:reorder', () => scheduleRevealRefresh(document));
    }

    const heroVisual = qs('.ip-campaign-hero .hero__visual');
    const heroInner = qs('.ip-campaign-hero .hero__inner');
    if (heroVisual && heroInner && window.matchMedia('(pointer: fine)').matches) {
      let heroFrame = 0;
      let heroX = 0;
      let heroY = 0;
      const syncHeroPointer = () => {
        heroFrame = 0;
        heroVisual.style.setProperty('--ip-motion-x', heroX.toFixed(3));
        heroVisual.style.setProperty('--ip-motion-y', heroY.toFixed(3));
      };

      heroInner.addEventListener('pointermove', (event) => {
        const rect = heroInner.getBoundingClientRect();
        heroX = ((event.clientX - rect.left) / rect.width - .5) * 2;
        heroY = ((event.clientY - rect.top) / rect.height - .5) * 2;
        if (!heroFrame) heroFrame = window.requestAnimationFrame(syncHeroPointer);
      }, { passive: true });

      heroInner.addEventListener('pointerleave', () => {
        heroX = 0;
        heroY = 0;
        if (!heroFrame) heroFrame = window.requestAnimationFrame(syncHeroPointer);
      });
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      const motionCards = qsa('.product-card-wrapper .card, .card-wrapper .card, .product-card, .bundle-product-card, .collection-card, .scent-card');
      motionCards.forEach((card) => {
        let cardFrame = 0;
        let pointerX = 50;
        let pointerY = 0;
        const syncCardPointer = () => {
          cardFrame = 0;
          card.style.setProperty('--ip-pointer-x', `${pointerX.toFixed(1)}%`);
          card.style.setProperty('--ip-pointer-y', `${pointerY.toFixed(1)}%`);
        };

        card.addEventListener('pointermove', (event) => {
          const rect = card.getBoundingClientRect();
          pointerX = ((event.clientX - rect.left) / rect.width) * 100;
          pointerY = ((event.clientY - rect.top) / rect.height) * 100;
          if (!cardFrame) cardFrame = window.requestAnimationFrame(syncCardPointer);
        }, { passive: true });

        card.addEventListener('pointerleave', () => {
          pointerX = 50;
          pointerY = 0;
          if (!cardFrame) cardFrame = window.requestAnimationFrame(syncCardPointer);
        });
      });
    }

    const motionImages = qsa('.product-card-wrapper img, .card-wrapper img, .product-card img, .hero-bottle img, .bundle-product-card img, .collection-card img, .brand-story__product img, .product__media img');
    motionImages.forEach((image, index) => {
      if (image.dataset.ipMedia === 'ready') return;
      image.dataset.ipMedia = 'pending';
      image.style.setProperty('--ip-media-index', String(Math.min(index % 6, 5)));
      const markReady = () => {
        image.dataset.ipMedia = 'ready';
        image.classList.add('ip-media-loaded');
      };
      if (image.complete && image.naturalWidth > 0) {
        window.setTimeout(markReady, Math.min(index % 6, 5) * 45);
      } else {
        image.addEventListener('load', markReady, { once: true });
      }
    });

    const root = document.documentElement;
    let scrollFrame = 0;
    const syncScrollMotion = () => {
      scrollFrame = 0;
      const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const progress = Math.min(1, Math.max(0, window.scrollY / maxScroll));
      root.style.setProperty('--ip-scroll-progress', progress.toFixed(4));
      document.documentElement.classList.toggle('ip-header-scrolled', window.scrollY > 12);
    };
    const scheduleScrollMotion = () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(syncScrollMotion);
    };
    syncScrollMotion();
    window.addEventListener('scroll', scheduleScrollMotion, { passive: true });
    window.addEventListener('resize', scheduleScrollMotion);
  }

  function initSuppressThirdPartyPopup() {
    const blockedCopy = [
      /GET\s+10%\s+OFF\s+YOUR\s+FIRST\s+ORDER/i,
      /new\s+product\s+drops/i,
      /Hi\s+there!\s+How\s+can\s+we\s+help\s+you\?/i,
      /Tap\s+here\s+to\s+chat\s+with\s+us/i,
      /^Click\s+here$/i
    ];

    const popupSelector = '[role="dialog"], [aria-modal="true"], .needsclick, [class*="popup"], [class*="Popup"], [class*="modal"], [class*="Modal"], [id*="popup"], [id*="modal"]';
    const textMatchesBlockedCopy = (text) => text && blockedCopy.some((pattern) => pattern.test(text));
    const looksLikePopupContainer = (node) => {
      if (!node || node.nodeType !== 1) return false;
      if (node === document.body || node === document.documentElement) return true;
      return Boolean(node.matches?.(popupSelector) || node.querySelector?.(popupSelector));
    };

    const isBlocked = (node) => {
      const text = node && node.textContent ? node.textContent.trim() : '';
      return textMatchesBlockedCopy(text);
    };

    const hidePopup = (node) => {
      if (!node || node.nodeType !== 1 || node === document.body || node === document.documentElement || !isBlocked(node)) return;
      let target = node.closest(popupSelector) || node;
      let walker = target;

      while (walker && walker !== document.body) {
        const styles = window.getComputedStyle(walker);
        const rect = walker.getBoundingClientRect();
        const zIndex = Number.parseInt(styles.zIndex, 10) || 0;
        if ((styles.position === 'fixed' || styles.position === 'absolute') && (zIndex >= 100 || (rect.width > 280 && rect.height > 180))) {
          target = walker;
        }
        walker = walker.parentElement;
      }

      target.setAttribute('aria-hidden', 'true');
      target.style.setProperty('display', 'none', 'important');
      target.style.setProperty('visibility', 'hidden', 'important');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open', 'overflow-hidden', 'no-scroll');
    };

    const scan = (root = document.body) => {
      if (!looksLikePopupContainer(root)) return;

      const candidates = [];
      if (root.matches?.(popupSelector)) candidates.push(root);
      candidates.push(...qsa(popupSelector, root));
      candidates.forEach((candidate) => {
        if (isBlocked(candidate)) {
          hidePopup(candidate);
          return;
        }
        qsa('*', candidate).forEach((node) => {
          if (isBlocked(node)) hidePopup(node);
        });
      });
    };

    scan();
    [400, 1200, 2600, 5000].forEach((delay) => window.setTimeout(scan, delay));

    let scanFrame = 0;
    const pendingRoots = new Set();
    const flushPendingScans = () => {
      scanFrame = 0;
      pendingRoots.forEach(scan);
      pendingRoots.clear();
    };
    const scheduleScan = (node) => {
      if (!looksLikePopupContainer(node)) return;
      pendingRoots.add(node);
      if (scanFrame) return;
      scanFrame = window.requestIdleCallback
        ? window.requestIdleCallback(flushPendingScans, { timeout: 1000 })
        : window.requestAnimationFrame(flushPendingScans);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          scheduleScan(node);
        });
      });
    });

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function initThirdPartyLazyImageFallback() {
    const hydrate = (root = document) => {
      qsa('img[data-src]', root).forEach((image) => {
        if (image.getAttribute('src') || !image.dataset.src) return;
        image.setAttribute('src', image.dataset.src);
        image.classList.remove('jdgm--loading');
      });
    };

    hydrate();

    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          hydrate(node.matches?.('img[data-src]') ? node.parentElement || document : node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initPolicyPlaceholderCleanup() {
    const roots = qsa('.shopify-policy__body .rte, .ip-legal-page .rte');
    if (!roots.length) return;

    const placeholderPattern = /\s*\[(?=[^\]]*(?:INSERT|ADD|INCLUDE|MODIFICATION|MODIFICATIONS|DO\/DO NOT|IF APPLICABLE|TRACKING|PAYMENT TYPES|SERVICE PROVIDERS|VENDORS|AGE|BUSINESS|CUSTOMER SUPPORT))[^\]]+\]\s*/gi;

    const cleanPolicyText = (value) => {
      const original = String(value || '');
      const keepLeadingSpace = /^\s/.test(original);
      const keepTrailingSpace = /\s$/.test(original);
      const cleaned = original
        .replace(/under the age of\s*\[(?=[^\]]*AGE)[^\]]+\]/gi, 'under the age of 13')
        .replace(/\[DO\/DO NOT\]/gi, 'do not')
        .replace(placeholderPattern, ' ')
        .replace(/\s+([,.;:])/g, '$1')
        .replace(/\(\s*\)/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

      if (!cleaned) return '';
      return `${keepLeadingSpace ? ' ' : ''}${cleaned}${keepTrailingSpace ? ' ' : ''}`;
    };

    roots.forEach((root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach((node) => {
        const cleaned = cleanPolicyText(node.nodeValue);
        if (cleaned !== node.nodeValue) node.nodeValue = cleaned;
      });

      qsa('p, li, h1, h2, h3, h4, h5, h6', root).forEach((node) => {
        if (!node.textContent.trim() && !node.querySelector('img, svg, table, iframe, input')) {
          node.remove();
        }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    const safelyInit = (init) => {
      try {
        init();
      } catch (error) {
        console.warn('iPerfume theme module failed to initialize:', error);
      }
    };
    const runWhenIdle = (callback) => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1600 });
      } else {
        window.setTimeout(callback, 0);
      }
    };

    [
      initHeaderMegaMenus,
      initCuratedMobileDrawer,
      initAnnouncement,
      initMobileMenu,
      initSearchModalReliability,
      initMobileDrawerEscapeReliability,
      initCheckoutHandoffReliability,
      initCart,
      initQuantitySelectors,
      initProductGallery,
      initFilters,
      initBundleBuilder,
      initQuiz,
      initTabbedProducts,
      initReviewCarousel,
      initMobileProductStickyAtc
    ].forEach(safelyInit);

    runWhenIdle(() => {
      [
        initSuppressThirdPartyPopup,
        initThirdPartyLazyImageFallback,
        initPolicyPlaceholderCleanup,
        initLuxuryMotion
      ].forEach(safelyInit);
    });
  });
})();
